import type { ApiSchema } from "../../apiSchema";
import { JsonContractError, readArray, readObject, readString, type JsonObject } from "../../http/jsonDecoding";
import { decodeItemSelector, readStrings } from "./selectorDecoders";
import { decodeProjectItemKind } from "./stepDecoders";

/** A derived records' kind, such as `corpus-vectors/1`: a lowercase name and a version. */
export function readRecordKind(object: JsonObject, field: string): string {
  const kind = readString(object, field);
  if (!/^[a-z][a-z0-9_-]{0,62}\/[1-9][0-9]{0,5}$/.test(kind)) throw new JsonContractError("A record kind is a lowercase name and a version, such as corpus-vectors/1.");
  return kind;
}

/** The closed scope grammar, edited as JSON by the existing selector form. */
export function decodeSourceScope(value: unknown, depth = 0): ApiSchema<"SourceScope"> {
  if (depth > 32) throw new JsonContractError("Source scopes may nest at most 32 levels.");
  const scope = readObject(value, "source scope");
  const keys = Object.keys(scope);
  if (keys.length !== 1) throw new JsonContractError("A source scope has exactly one form.");
  const nested = (part: unknown) => decodeSourceScope(part, depth + 1);
  switch (keys[0]) {
    case "corpus":
      if (scope["corpus"] !== "bound") throw new JsonContractError('A corpus scope names "bound".');
      return { corpus: "bound" };
    case "selection":
      if (scope["selection"] !== "bound") throw new JsonContractError('A selection scope names "bound".');
      return { selection: "bound" };
    case "documents": return { documents: readStrings(scope["documents"], "documents") };
    case "metadata": {
      const values = readObject(scope["metadata"], "metadata");
      return { metadata: Object.fromEntries(Object.keys(values).map((key) => [key, readString(values, key)])) };
    }
    case "segment_refs": return { segment_refs: readStrings(scope["segment_refs"], "segment references") };
    case "segments": {
      const segments = readObject(scope["segments"], "segments");
      return { segments: { type: readString(segments, "type"), within: nested(segments["within"]) } };
    }
    case "union": return { union: readArray(scope["union"], "union", nested) };
    case "intersection": return { intersection: readArray(scope["intersection"], "intersection", nested) };
    case "except": {
      const parts = readArray(scope["except"], "except", nested);
      const [left, right] = parts;
      if (parts.length !== 2 || left === undefined || right === undefined) throw new JsonContractError("Except needs two scopes.");
      return { except: [left, right] };
    }
    case "items": {
      const items = readObject(scope["items"], "items");
      const filter = items["filter"] === undefined ? {} : readObject(items["filter"], "item filter");
      if (filter["accepted"] !== undefined && filter["accepted"] !== null && filter["accepted"] !== true) throw new JsonContractError("An accepted filter is true or null.");
      return { items: { kind: decodeProjectItemKind(items["kind"]), filter: { tags: filter["tags"] === undefined ? [] : readStrings(filter["tags"], "tags"), accepted: filter["accepted"] === true ? true : null } } };
    }
    case "terms": {
      const terms = readObject(scope["terms"], "terms");
      const termKinds = readArray(terms["term_kinds"], "term kinds", (kind): ApiSchema<"TermsOf">["term_kinds"][number] => {
        if (kind !== "class" && kind !== "object_property" && kind !== "datatype_property" && kind !== "annotation_property" && kind !== "individual") throw new JsonContractError("Unknown ontology term kind.");
        return kind;
      });
      return { terms: { term_kinds: termKinds, modules: terms["modules"] === undefined ? [] : readArray(terms["modules"], "modules", (module) => decodeItemSelector(module, "module")) } };
    }
    case "without_records": {
      const without = readObject(scope["without_records"], "documents without records");
      return { without_records: { within: nested(without["within"]), kind: readRecordKind(without, "kind") } };
    }
    case undefined:
    default: throw new JsonContractError("Unknown source scope form.");
  }
}
